import type {
  AnalyticsProvider,
  OnUserChangeFunction,
  OnUserPropertiesChangeFunction,
  TrackEventFunction,
} from "@/lib/analytics/analytics";

export class AnalyticsService {
  private initializedProviders: AnalyticsProvider[] = [];
  private callQueue: Array<() => void> = [];

  constructor(private readonly providers: AnalyticsProvider[]) {
    // Schedule provider initialization to run during browser idle time
    // This prevents blocking user interactions during app startup;
    const init = () => {
      this.providers.forEach((instance) => {
        if (instance.init()) {
          this.initializedProviders.push(instance);
        }
      });

      this.callQueue.forEach((queuedCall) => queuedCall());
      this.callQueue = [];
    };

    // Use requestIdleCallback if available (Chrome, Firefox, Edge, Opera);
    // Timeout ensures execution within 2 seconds even on busy pages;
    // Fall back to setTimeout(0) for Safari/iOS which don't support requestIdleCallback;

    // @ts-expect-error "requestIdleCallback" is considered to always exist
    if (window.requestIdleCallback) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 0);
    }
  }

  private executeIfInitialized(callback: (instance: AnalyticsProvider) => void) {
    if (!this.initializedProviders.length) {
      console.warn("There are no initialized analytics providers.");
      return;
    }

    this.initializedProviders.forEach(callback);
  }

  public trackEvent: TrackEventFunction = (eventName, properties) => {
    this.executeIfInitialized((instance) => {
      instance.trackEvent(eventName, properties);
    });
  };

  public trackUserChange: OnUserChangeFunction = (userData) => {
    this.executeIfInitialized((instance) => {
      instance.onUserChange?.(userData);
    });
  };

  public trackUserPropertiesChange: OnUserPropertiesChangeFunction = (clientData) => {
    this.executeIfInitialized((instance) => {
      instance.onUserPropertiesChange?.(clientData);
    });
  };
}
